import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
} from "@chakra-ui/react";
import { EnumOptionsType, RJSFSchema, WidgetProps } from "@rjsf/utils";
import type { JSONSchema7Type } from "json-schema";

import { RanaButton } from "@nens/ranalib";

type Option = JSONSchema7Type | EnumOptionsType<RJSFSchema>;

const optionIsObject = (option: Option): option is EnumOptionsType<RJSFSchema> =>
  typeof option === "object" && !Array.isArray(option);

export function ProcessRunFormEnumSelect<T>(props: WidgetProps<T>) {
  const {
    schema,
    value,
    onChange,
    errorSchema,
    uiSchema,
    enumDisabled,
    options,
    readonly,
    disabled,
  } = props;
  const isDisabled = readonly || disabled;
  const enumOptions = schema.enum || options?.enumOptions;
  const hasError = errorSchema?.__errors && errorSchema.__errors.length > 0;

  if (!enumOptions) {
    console.warn("ProcessRunFormEnumSelect: No enum options found");
    return null;
  }

  const getValueAndLabel = (option: Option) => {
    const value = optionIsObject(option) ? option?.value : option;
    if (value === undefined) return { value: undefined, label: undefined };
    const optionMatch = enumOptions.find(
      (opt) => (optionIsObject(opt) && opt.value === value) || opt === value
    );
    if (!optionMatch) return { value: undefined, label: undefined };
    const label = optionIsObject(optionMatch) ? optionMatch?.label : optionMatch?.toString();
    return { value, label };
  };

  // Get default value from UI schema or schema or fallback to first option
  const defaultValue =
    schema.default || uiSchema?.["ui:options"]?.default || getValueAndLabel(enumOptions[0]).value;

  // Set default value if value is undefined
  if (value === undefined && defaultValue !== undefined) {
    onChange(defaultValue);
  }

  return (
    <Menu matchWidth>
      <MenuButton
        as={RanaButton}
        isDisabled={isDisabled}
        variant="tertiary"
        w="full"
        borderColor={hasError ? "semantic.error.main" : "inherit"}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Box>{getValueAndLabel(value === undefined ? defaultValue : value)?.label}</Box>{" "}
          {!isDisabled && <ChevronDownIcon mt={1} />}
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList>
          <MenuOptionGroup onChange={onChange} value={value}>
            {enumOptions.map((option) => {
              const { value, label } = getValueAndLabel(option);
              if (value === undefined) return null;
              return (
                <MenuItemOption
                  value={value}
                  isDisabled={enumDisabled?.includes(value)}
                  key={value + ""}
                >
                  {label}
                </MenuItemOption>
              );
            })}
          </MenuOptionGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
}
